const PDF_MIME_TYPE = 'application/pdf';
const DEFAULT_RENDER_SCALE = 2;
const DEFAULT_MAX_DIMENSION = 2200;
const DEFAULT_OUTPUT_TYPE = 'image/jpeg';
const DEFAULT_OUTPUT_QUALITY = 0.92;
const PDFJS_DIST_VERSION = '4.10.38';
const CDN_WORKER_SRC = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_DIST_VERSION}/legacy/build/pdf.worker.min.mjs`;

let cachedPdfModulePromise = null;

const isLocalDevelopmentHost = () => {
  if (typeof window === 'undefined') return false;
  const hostname = String(window.location?.hostname || '').toLowerCase();
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

const resolveBundledWorkerSrc = () => {
  try {
    return new URL('pdfjs-dist/legacy/build/pdf.worker.min.mjs', import.meta.url).toString();
  } catch (bundleError) {
    const publicPath = process.env.PUBLIC_URL;
    if (publicPath !== undefined) {
      return `${String(publicPath || '').replace(/\/$/, '')}/pdf.worker.min.mjs`;
    }

    console.warn('Unable to resolve bundled PDF worker path:', bundleError);
    return null;
  }
};

const resolvePdfWorkerSrc = () => {
  const configuredWorker = String(process.env.REACT_APP_PDF_WORKER_SRC || '').trim();
  if (configuredWorker) return configuredWorker;

  // In production, prefer a stable CDN URL to avoid hashed-worker cache mismatches.
  if (!isLocalDevelopmentHost()) {
    return CDN_WORKER_SRC;
  }

  const bundledWorker = resolveBundledWorkerSrc();
  return bundledWorker || CDN_WORKER_SRC;
};

const isWorkerLoadError = (error) => {
  const message = String(error?.message || error || '').toLowerCase();
  return (
    message.includes('setting up fake worker failed') ||
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('pdf.worker')
  );
};

const configureWorkerSrc = (pdfJsModule, workerSrc) => {
  if (!pdfJsModule?.GlobalWorkerOptions || !workerSrc) return;
  pdfJsModule.GlobalWorkerOptions.workerSrc = workerSrc;
  console.log('PDF.js worker source configured:', workerSrc);
};

const loadPdfJsModule = async () => {
  if (!cachedPdfModulePromise) {
    cachedPdfModulePromise = import('pdfjs-dist/legacy/build/pdf.mjs');
  }

  return cachedPdfModulePromise;
};

const clampRenderScale = (viewport, scale, maxDimension) => {
  const safeScale = Number.isFinite(scale) && scale > 0 ? scale : DEFAULT_RENDER_SCALE;
  const safeMaxDimension = Number.isFinite(maxDimension) && maxDimension > 0
    ? maxDimension
    : DEFAULT_MAX_DIMENSION;

  const maxSideAtScale = Math.max(viewport.width, viewport.height) * safeScale;
  if (maxSideAtScale <= safeMaxDimension) return safeScale;
  return safeMaxDimension / Math.max(viewport.width, viewport.height);
};

const canvasToBlob = (canvas, mimeType, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to convert PDF page to image blob'));
          return;
        }
        resolve(blob);
      },
      mimeType,
      quality
    );
  });

export const isPdfFile = (file) => {
  if (!file) return false;
  const fileType = String(file.type || '').toLowerCase();
  const fileName = String(file.name || '').toLowerCase();
  return fileType === PDF_MIME_TYPE || fileName.endsWith('.pdf');
};

export const convertPdfToImageFile = async (pdfFile, options = {}) => {
  if (!isPdfFile(pdfFile)) {
    throw new Error('convertPdfToImageFile requires a PDF file');
  }

  const {
    pageNumber = 1,
    scale = DEFAULT_RENDER_SCALE,
    maxDimension = DEFAULT_MAX_DIMENSION,
    outputType = DEFAULT_OUTPUT_TYPE,
    outputQuality = DEFAULT_OUTPUT_QUALITY
  } = options;

  const pdfBytes = await pdfFile.arrayBuffer();
  const pdfJsModule = await loadPdfJsModule();

  const renderWithCurrentWorker = async () => {
    const loadingTask = pdfJsModule.getDocument({ data: pdfBytes });
    const pdfDocument = await loadingTask.promise;
    const targetPageNumber = Math.min(Math.max(1, pageNumber), pdfDocument.numPages || 1);
    const page = await pdfDocument.getPage(targetPageNumber);

    const baseViewport = page.getViewport({ scale: 1 });
    const renderScale = clampRenderScale(baseViewport, scale, maxDimension);
    const viewport = page.getViewport({ scale: renderScale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);

    const context = canvas.getContext('2d', { alpha: false });
    if (!context) {
      throw new Error('Failed to create canvas context for PDF rendering');
    }

    await page.render({
      canvasContext: context,
      viewport
    }).promise;

    const imageBlob = await canvasToBlob(canvas, outputType, outputQuality);
    const convertedName = String(pdfFile.name || 'receipt')
      .replace(/\.pdf$/i, '')
      .concat(`-page-${targetPageNumber}.jpg`);

    const convertedFile = new File([imageBlob], convertedName, {
      type: outputType,
      lastModified: Date.now()
    });

    try {
      page.cleanup();
    } catch (_) {
      // no-op
    }

    try {
      await pdfDocument.destroy();
    } catch (_) {
      // no-op
    }

    canvas.width = 0;
    canvas.height = 0;

    return convertedFile;
  };

  const primaryWorkerSrc = resolvePdfWorkerSrc();
  configureWorkerSrc(pdfJsModule, primaryWorkerSrc);

  try {
    return await renderWithCurrentWorker();
  } catch (error) {
    if (isWorkerLoadError(error) && primaryWorkerSrc !== CDN_WORKER_SRC) {
      console.warn('Primary PDF worker failed; retrying with CDN worker fallback');
      configureWorkerSrc(pdfJsModule, CDN_WORKER_SRC);
      return renderWithCurrentWorker();
    }

    throw error;
  }
};

export const extractTextFromPdfFile = async (pdfFile, options = {}) => {
  if (!isPdfFile(pdfFile)) {
    throw new Error('extractTextFromPdfFile requires a PDF file');
  }

  const {
    maxPages = 3
  } = options;

  const pdfBytes = await pdfFile.arrayBuffer();
  const pdfJsModule = await loadPdfJsModule();
  const primaryWorkerSrc = resolvePdfWorkerSrc();
  configureWorkerSrc(pdfJsModule, primaryWorkerSrc);

  const extractWithCurrentWorker = async () => {
    const loadingTask = pdfJsModule.getDocument({ data: pdfBytes });
    const pdfDocument = await loadingTask.promise;
    const pageCount = Math.min(Math.max(1, maxPages), pdfDocument.numPages || 1);
    const pages = [];

    try {
      for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
        const page = await pdfDocument.getPage(pageNumber);
        const textContent = await page.getTextContent();
        const pageText = (textContent.items || [])
          .map((item) => item?.str || '')
          .join('\n')
          .replace(/[ \t]+\n/g, '\n')
          .replace(/\n{3,}/g, '\n\n')
          .trim();

        if (pageText) {
          pages.push(pageText);
        }

        try {
          page.cleanup();
        } catch (_) {
          // no-op
        }
      }
    } finally {
      try {
        await pdfDocument.destroy();
      } catch (_) {
        // no-op
      }
    }

    return pages.join('\n\n');
  };

  try {
    return await extractWithCurrentWorker();
  } catch (error) {
    if (isWorkerLoadError(error) && primaryWorkerSrc !== CDN_WORKER_SRC) {
      console.warn('Primary PDF worker failed during text extraction; retrying with CDN worker fallback');
      configureWorkerSrc(pdfJsModule, CDN_WORKER_SRC);
      return extractWithCurrentWorker();
    }

    throw error;
  }
};

export const extractTextRowsFromPdfFile = async (pdfFile, options = {}) => {
  if (!isPdfFile(pdfFile)) {
    throw new Error('extractTextRowsFromPdfFile requires a PDF file');
  }

  const {
    maxPages = 12,
    yTolerance = 3
  } = options;

  const pdfBytes = await pdfFile.arrayBuffer();
  const pdfJsModule = await loadPdfJsModule();
  const primaryWorkerSrc = resolvePdfWorkerSrc();
  configureWorkerSrc(pdfJsModule, primaryWorkerSrc);

  const extractRowsWithCurrentWorker = async () => {
    const loadingTask = pdfJsModule.getDocument({ data: pdfBytes });
    const pdfDocument = await loadingTask.promise;
    const pageCount = Math.min(Math.max(1, maxPages), pdfDocument.numPages || 1);
    const rows = [];

    try {
      for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
        const page = await pdfDocument.getPage(pageNumber);
        const textContent = await page.getTextContent();
        const items = (textContent.items || [])
          .map((item) => {
            const transform = Array.isArray(item?.transform) ? item.transform : [];
            return {
              text: String(item?.str || '').trim(),
              x: Number(transform[4]) || 0,
              y: Number(transform[5]) || 0
            };
          })
          .filter((item) => item.text);

        const groupedRows = [];
        items.forEach((item) => {
          const row = groupedRows.find((candidate) =>
            Math.abs(candidate.y - item.y) <= yTolerance
          );
          if (row) {
            row.items.push(item);
            row.y = (row.y + item.y) / 2;
          } else {
            groupedRows.push({
              y: item.y,
              items: [item]
            });
          }
        });

        groupedRows
          .sort((a, b) => b.y - a.y)
          .forEach((row) => {
            const text = row.items
              .sort((a, b) => a.x - b.x)
              .map((item) => item.text)
              .join(' ')
              .replace(/\s{2,}/g, ' ')
              .trim();
            if (text) {
              rows.push({
                pageNumber,
                text,
                items: row.items
                  .sort((a, b) => a.x - b.x)
                  .map((item) => ({
                    text: item.text,
                    x: item.x,
                    y: item.y
                  }))
              });
            }
          });

        try {
          page.cleanup();
        } catch (_) {
          // no-op
        }
      }
    } finally {
      try {
        await pdfDocument.destroy();
      } catch (_) {
        // no-op
      }
    }

    return rows;
  };

  try {
    return await extractRowsWithCurrentWorker();
  } catch (error) {
    if (isWorkerLoadError(error) && primaryWorkerSrc !== CDN_WORKER_SRC) {
      console.warn('Primary PDF worker failed during row extraction; retrying with CDN worker fallback');
      configureWorkerSrc(pdfJsModule, CDN_WORKER_SRC);
      return extractRowsWithCurrentWorker();
    }

    throw error;
  }
};
